import { useState, useEffect, useRef } from 'react';
import { useWakeLock } from '@/hooks/useWakeLock';
import { useVoiceGuidance } from '@/hooks/useVoiceGuidance';
import { X, Play, Pause, Volume2, VolumeX, Home, ChevronUp, ChevronDown, RotateCcw } from 'lucide-react';
import { Slider } from '@/components/ui/slider';
import { StepperInput } from './StepperInput';
import BreathingBar from './BreathingBar';
import { EXERCISE_BACKGROUNDS, EXERCISE_BG_FALLBACK_COLOR, CO2_WATERMARK_LOGO } from '@/utils/exerciseBackgrounds';
import AirHungerRatingModal from './AirHungerRatingModal';
import ExerciseInstructionsDrawer from './ExerciseInstructionsDrawer';
import ExerciseTopLogo from './ExerciseTopLogo';
import InhalationGentleWarning from './InhalationGentleWarning';

const ReduceFrequencyModal = ({ isOpen, onClose, onSessionComplete }) => {
  const [isActive, setIsActive] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [phase, setPhase] = useState('ready'); // 'ready', 'inhale', 'exhale'
  useWakeLock(isActive && !isPaused);
  useVoiceGuidance(phase, isActive && !isPaused);
  const [progress, setProgress] = useState(0);
  const [soundEnabled, setSoundEnabled] = useState(true);
  const [breathDuration, setBreathDuration] = useState(2); // Duration for both inhale and exhale
  const [cyclesCompleted, setCyclesCompleted] = useState(0);
  const [elapsedTime, setElapsedTime] = useState(0);
  const [showAirHungerModal, setShowAirHungerModal] = useState(false);
  const [lastSessionId, setLastSessionId] = useState(null);
  const [pendingClose, setPendingClose] = useState(false);
  const [ratedThisSession, setRatedThisSession] = useState(false);
  
  const intervalRef = useRef(null);
  const timerRef = useRef(null);
  const audioContextRef = useRef(null);
  const soundEnabledRef = useRef(soundEnabled);

  // Keep ref in sync with state
  useEffect(() => {
    soundEnabledRef.current = soundEnabled;
  }, [soundEnabled]);

  // Restoration of the last-used breath duration is intentionally DISABLED
  // on this exercise: the therapeutic progression requires always starting
  // slow (2 s) and ramping up gradually within the session, so each new
  // launch must reset to the default.
  // useLastSessionSettings(isOpen, 'reduce-frequency', ...)  — kept out on purpose.

  // Initialize audio context
  useEffect(() => {
    audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
    return () => {
      if (audioContextRef.current) {
        audioContextRef.current.close();
      }
    };
  }, []);

  const playTone = (frequency, duration) => {
    if (!audioContextRef.current || !soundEnabledRef.current) return;
    
    try {
      const oscillator = audioContextRef.current.createOscillator();
      const gainNode = audioContextRef.current.createGain();
      
      oscillator.connect(gainNode);
      gainNode.connect(audioContextRef.current.destination);
      
      oscillator.frequency.value = frequency;
      oscillator.type = 'sine';
      
      gainNode.gain.setValueAtTime(0.3, audioContextRef.current.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(0.01, audioContextRef.current.currentTime + duration);
      
      oscillator.start(audioContextRef.current.currentTime);
      oscillator.stop(audioContextRef.current.currentTime + duration);
    } catch (error) {
      console.error('Audio error:', error);
    }
  };

  const playMetronomeTick = () => {
    if (!audioContextRef.current || !soundEnabledRef.current) return;
    
    try {
      const oscillator = audioContextRef.current.createOscillator();
      const gainNode = audioContextRef.current.createGain();
      
      oscillator.connect(gainNode);
      gainNode.connect(audioContextRef.current.destination);
      
      oscillator.frequency.value = 800;
      oscillator.type = 'sine';
      
      gainNode.gain.setValueAtTime(0.15, audioContextRef.current.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(0.01, audioContextRef.current.currentTime + 0.05);
      
      oscillator.start(audioContextRef.current.currentTime);
      oscillator.stop(audioContextRef.current.currentTime + 0.05);
    } catch (error) {
      console.error('Audio error:', error);
    }
  };

  // Main breathing cycle effect
  useEffect(() => {
    if (!isOpen || !isActive || isPaused) {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      return;
    }

    const totalDuration = breathDuration * 1000; // in ms
    const intervalTime = 50; // update every 50ms
    const increment = (100 / totalDuration) * intervalTime;

    intervalRef.current = setInterval(() => {
      setProgress(prev => {
        const newProgress = prev + increment;
        
        if (newProgress >= 100) {
          // Play sound at phase transition
          if (soundEnabledRef.current) {
            if (phase === 'inhale') {
              playTone(440, 0.15); // Higher tone for exhale start
            } else {
              playTone(330, 0.15); // Lower tone for inhale start
            }
          }
          
          // Switch phase
          if (phase === 'inhale') {
            setPhase('exhale');
          } else {
            setPhase('inhale');
            setCyclesCompleted(c => c + 1);
          }
          
          return 0;
        }
        return newProgress;
      });
    }, intervalTime);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [isOpen, isActive, isPaused, phase, breathDuration]);

  // Elapsed time counter
  useEffect(() => {
    if (!isOpen || !isActive || isPaused) {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
      return;
    }

    timerRef.current = setInterval(() => {
      setElapsedTime(prev => prev + 1);
      if (soundEnabledRef.current) {
        playMetronomeTick();
      }
    }, 1000);

    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, [isOpen, isActive, isPaused]);

  const startSession = () => {
    setIsActive(true);
    setIsPaused(false);
    setPhase('inhale');
    setProgress(0);
    setCyclesCompleted(0);
    setElapsedTime(0);
    setRatedThisSession(false);
    if (soundEnabledRef.current) {
      playTone(330, 0.2);
    }
  };

  const handleTogglePause = async () => {
    const goingToPause = !isPaused;
    setIsPaused(!isPaused);
    if (goingToPause && !ratedThisSession && elapsedTime > 10) {
      setRatedThisSession(true);
      let savedSession = null;
      if (onSessionComplete) {
        savedSession = await onSessionComplete({
          patternId: 'reduce-frequency',
          patternName: 'Respiration Réduite',
          durationSeconds: elapsedTime,
          cyclesCompleted: cyclesCompleted,
          settings: { breathDuration },
        });
      }
      setLastSessionId(savedSession?.id || null);
      setShowAirHungerModal(true);
    }
  };

  const stopSession = async (saveSession = false) => {
    let savedSession = null;
    if (saveSession && onSessionComplete && elapsedTime > 10) {
      savedSession = await onSessionComplete({
        patternId: 'reduce-frequency',
        patternName: 'Respiration Réduite',
        durationSeconds: elapsedTime,
        cyclesCompleted: cyclesCompleted,
        settings: { breathDuration },
      });
    }
    setIsActive(false);
    setIsPaused(false);
    setPhase('ready');
    setProgress(0);
    return savedSession;
  };

  const applyAirHungerRecommendation = (direction) => {
    // Increase = longer breaths (harder), Decrease = shorter breaths (easier)
    setBreathDuration((prev) => {
      if (direction === 'up') return Math.min(prev + 1, 30);
      if (direction === 'down-strong') return Math.max(prev - 3, 1);
      return prev;
    });
  };

  // Reset session (go back to settings without saving)
  const resetSession = () => {
    setIsActive(false);
    setIsPaused(false);
    setPhase('ready');
    setProgress(0);
    setCyclesCompleted(0);
    setElapsedTime(0);
  };

  const handleClose = async () => {
    const wasLongSession = elapsedTime > 10;
    if (wasLongSession && !ratedThisSession) {
      const saved = await stopSession(true);
      setLastSessionId(saved?.id || null);
      setPendingClose(true);
      setRatedThisSession(true);
      setShowAirHungerModal(true);
    } else {
      stopSession(false);
      onClose();
    }
  };

  const handleAirHungerClose = () => {
    setShowAirHungerModal(false);
    if (pendingClose) {
      setPendingClose(false);
      onClose();
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Calculate breathing rate
  const breathingRate = breathDuration > 0 ? (60 / (breathDuration * 2)).toFixed(1) : 0;

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/70 backdrop-blur-sm">
      <div 
        className="relative w-full h-full flex flex-col"
        style={{
          backgroundImage: `url(${EXERCISE_BACKGROUNDS.reduceFrequency})`,
          backgroundColor: '#EAF4FB',
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
      >
        {/* Soft white veil to match the cabinet homepage rendering */}
        <div aria-hidden="true" className="pointer-events-none absolute inset-0 z-[1]" style={{ background: 'linear-gradient(180deg, rgba(255,255,255,0.55) 0%, rgba(240,247,252,0.55) 55%, rgba(214,235,246,0.55) 100%)' }} />
        {/* Floating Navigation Buttons */}
        <div className="absolute top-4 left-4 z-20">
          <button
            onClick={handleClose}
            className="p-3 bg-slate-900/10 hover:bg-slate-900/20 backdrop-blur-md rounded-full transition-colors"
            title="Retour aux exercices"
          >
            <Home size={20} className="text-slate-700" />
          </button>
        </div>
        
        <div className="absolute top-4 right-4 z-20">
          <button
            onClick={handleClose}
            className="p-3 bg-slate-900/10 hover:bg-slate-900/20 backdrop-blur-md rounded-full transition-colors"
            title="Fermer"
          >
            <X size={20} className="text-slate-700" />
          </button>
        </div>

        <ExerciseTopLogo />

        {/* Main Content */}
        <div className="relative z-10 w-full h-full overflow-y-auto px-4 pt-16 pb-8 flex flex-col lg:items-center lg:justify-center lg:pb-8">

          {/* ================= DESKTOP (lg+) — 3-column grid, capsule dead-center ================= */}
          <div className="hidden lg:grid w-full grid-cols-[1fr_auto_1fr] items-center gap-6">
            {/* LEFT — Stats + Duration */}
            <div className="justify-self-end w-full max-w-xs space-y-4">
              <div className="grid grid-cols-1 gap-3">
                <div className="bg-white/85 backdrop-blur-md rounded-xl px-3 py-3 text-center border border-slate-300 shadow-md">
                  <p className="text-slate-500 text-xs mb-1">Temps</p>
                  <p className="text-slate-800 font-bold text-xl">{formatTime(elapsedTime)}</p>
                </div>
                <div className="bg-white/85 backdrop-blur-md rounded-xl px-3 py-3 text-center border border-slate-300 shadow-md">
                  <p className="text-slate-500 text-xs mb-1">Cycles</p>
                  <p className="text-slate-800 font-bold text-xl">{cyclesCompleted}</p>
                </div>
                <div className="bg-white/85 backdrop-blur-md rounded-xl px-3 py-3 text-center border border-slate-300 shadow-md">
                  <p className="text-slate-500 text-xs mb-1">Fréquence</p>
                  <p className="text-slate-800 font-bold text-xl">{breathingRate}<span className="text-sm">/min</span></p>
                </div>
              </div>
              <div className="bg-white/85 backdrop-blur-md rounded-xl px-4 py-3 border border-slate-300 shadow-md">
                <p className="text-slate-600 text-xs text-center font-medium mb-2">Durée inspir / expir</p>
                <StepperInput
                  value={breathDuration} onChange={(v) => setBreathDuration(v)}
                  min={1} max={30} step={1} unit="sec" label="durée inspir/expir" colorClass="cyan"
                  valueTestId="reduce-freq-duration-value" incTestId="reduce-freq-duration-plus" decTestId="reduce-freq-duration-minus"
                />
              </div>
            </div>

            {/* CENTER — Capsule only */}
            <div className="flex flex-col items-center">
              <div className="relative">
                <InhalationGentleWarning visible={phase === 'inhale'} />
                <BreathingBar
                  phase={phase}
                  progress={progress / 100}
                  phaseDurations={{ inhale: breathDuration, exhale: breathDuration }}
                  colors={{ inhale: '#22d3ee', exhale: '#f97316', ready: '#64748b' }}
                  lightTheme
                />
                <div className="absolute left-1/2 -translate-x-1/2 top-full mt-3 text-center whitespace-nowrap">
                  <p className="text-slate-800 font-semibold text-lg">
                    {phase === 'ready' ? 'Prêt' : phase === 'inhale' ? 'Inspirez' : 'Expirez'}
                  </p>
                  <p className="text-slate-500 text-sm">{breathDuration}s / {breathDuration}s</p>
                </div>
              </div>
            </div>

            {/* RIGHT — Vertical controls (Play / Sound / Reset) */}
            <div className="justify-self-start flex flex-col items-center gap-4">
              <button
                onClick={() => { if (!isActive) { startSession(); } else { handleTogglePause(); } }}
                className="w-20 h-20 rounded-full bg-white/80 backdrop-blur shadow-lg flex items-center justify-center hover:bg-white transition-colors"
                style={{ border: '3px solid rgba(34,211,238,0.5)' }}
                data-testid="reduce-freq-play-btn-desktop"
              >
                {!isActive || isPaused ? <Play className="w-9 h-9 text-slate-600 ml-1" /> : <Pause className="w-9 h-9 text-slate-600" />}
              </button>
              <button
                onClick={() => setSoundEnabled(!soundEnabled)}
                className="w-14 h-14 rounded-full bg-white/80 backdrop-blur shadow-lg flex items-center justify-center hover:bg-white transition-colors"
                style={{ border: '3px solid rgba(34,211,238,0.5)' }}
                data-testid="reduce-freq-sound-btn-desktop"
              >
                {soundEnabled ? <Volume2 className="w-6 h-6 text-slate-600" /> : <VolumeX className="w-6 h-6 text-slate-400" />}
              </button>
              {isActive && (
                <button
                  onClick={async () => {
                    const wasLong = elapsedTime > 10;
                    const saved = await stopSession(wasLong && !ratedThisSession);
                    if (wasLong && !ratedThisSession) {
                      setLastSessionId(saved?.id || null);
                      setRatedThisSession(true);
                      setShowAirHungerModal(true);
                    }
                  }}
                  className="w-14 h-14 rounded-full bg-red-500/80 backdrop-blur shadow-lg flex items-center justify-center hover:bg-red-500 transition-colors"
                  title="Arrêter"
                  data-testid="reduce-freq-stop-btn-desktop"
                >
                  <div className="w-5 h-5 bg-white rounded-sm" />
                </button>
              )}
              {isActive && phase !== 'ready' && (
                <button
                  onClick={resetSession}
                  className="w-14 h-14 rounded-full bg-white/80 backdrop-blur shadow-lg flex items-center justify-center hover:bg-white transition-colors"
                  style={{ border: '3px solid rgba(239, 68, 68, 0.5)' }}
                  title="Réinitialiser"
                  data-testid="reduce-freq-reset-btn-desktop"
                >
                  <RotateCcw className="w-6 h-6 text-red-500" />
                </button>
              )}
            </div>
          </div>

          {/* ================= MOBILE / TABLET (<lg) — stacked ================= */}
          <div className="lg:hidden flex flex-col items-center my-auto gap-6 mx-auto">
            <div className="grid grid-cols-3 gap-3 w-full max-w-xs">
              <div className="bg-white/85 backdrop-blur-md rounded-xl px-3 py-3 text-center border border-slate-300 shadow-md">
                <p className="text-slate-500 text-xs mb-1">Temps</p>
                <p className="text-slate-800 font-bold text-lg">{formatTime(elapsedTime)}</p>
              </div>
              <div className="bg-white/85 backdrop-blur-md rounded-xl px-3 py-3 text-center border border-slate-300 shadow-md">
                <p className="text-slate-500 text-xs mb-1">Cycles</p>
                <p className="text-slate-800 font-bold text-lg">{cyclesCompleted}</p>
              </div>
              <div className="bg-white/85 backdrop-blur-md rounded-xl px-3 py-3 text-center border border-slate-300 shadow-md">
                <p className="text-slate-500 text-xs mb-1">Fréquence</p>
                <p className="text-slate-800 font-bold text-lg">{breathingRate}<span className="text-sm">/min</span></p>
              </div>
            </div>

            <div className="flex flex-col items-center">
              <div className="relative flex items-center gap-3 sm:gap-4">
                <InhalationGentleWarning visible={phase === 'inhale'} />
                <BreathingBar
                  phase={phase}
                  progress={progress / 100}
                  phaseDurations={{ inhale: breathDuration, exhale: breathDuration }}
                  colors={{ inhale: '#22d3ee', exhale: '#f97316', ready: '#64748b' }}
                  lightTheme
                />
                <div className="bg-white/85 backdrop-blur-md rounded-xl px-2 py-3 border border-slate-300 shadow-md">
                  <p className="text-slate-600 text-xs text-center font-medium mb-2">Durée</p>
                  <StepperInput
                    value={breathDuration} onChange={(v) => setBreathDuration(v)}
                    min={1} max={30} step={1} unit="sec" label="durée inspir/expir" colorClass="cyan"
                    valueTestId="reduce-freq-duration-value" incTestId="reduce-freq-duration-plus" decTestId="reduce-freq-duration-minus"
                  />
                </div>
                <div className="absolute left-1/2 -translate-x-1/2 top-full mt-3 text-center whitespace-nowrap">
                  <p className="text-slate-800 font-semibold text-lg">
                    {phase === 'ready' ? 'Prêt' : phase === 'inhale' ? 'Inspirez' : 'Expirez'}
                  </p>
                  <p className="text-slate-500 text-sm">{breathDuration}s / {breathDuration}s</p>
                </div>
              </div>
            </div>

            <div className="flex flex-row gap-3 sm:gap-4 exercise-controls-mobile-fixed">
              <button
                onClick={() => { if (!isActive) { startSession(); } else { handleTogglePause(); } }}
                className="w-16 h-16 sm:w-20 sm:h-20 rounded-full bg-white/80 backdrop-blur shadow-lg flex items-center justify-center hover:bg-white transition-colors"
                style={{ border: '3px solid rgba(34,211,238,0.5)' }}
                data-testid="reduce-freq-play-btn"
              >
                {!isActive || isPaused ? <Play className="w-7 h-7 sm:w-9 sm:h-9 text-slate-600 ml-1" /> : <Pause className="w-7 h-7 sm:w-9 sm:h-9 text-slate-600" />}
              </button>
              <button
                onClick={() => setSoundEnabled(!soundEnabled)}
                className="w-16 h-16 sm:w-20 sm:h-20 rounded-full bg-white/80 backdrop-blur shadow-lg flex items-center justify-center hover:bg-white transition-colors"
                style={{ border: '3px solid rgba(34,211,238,0.5)' }}
                data-testid="reduce-freq-sound-btn"
              >
                {soundEnabled ? <Volume2 className="w-6 h-6 sm:w-7 sm:h-7 text-slate-600" /> : <VolumeX className="w-6 h-6 sm:w-7 sm:h-7 text-slate-400" />}
              </button>
              {isActive && (
                <button
                  onClick={async () => {
                    const wasLong = elapsedTime > 10;
                    const saved = await stopSession(wasLong && !ratedThisSession);
                    if (wasLong && !ratedThisSession) {
                      setLastSessionId(saved?.id || null);
                      setRatedThisSession(true);
                      setShowAirHungerModal(true);
                    }
                  }}
                  className="w-16 h-16 sm:w-20 sm:h-20 rounded-full bg-red-500/80 backdrop-blur shadow-lg flex items-center justify-center hover:bg-red-500 transition-colors"
                  title="Arrêter"
                  data-testid="reduce-freq-stop-btn"
                >
                  <div className="w-5 h-5 sm:w-6 sm:h-6 bg-white rounded-sm" />
                </button>
              )}
              {isActive && phase !== 'ready' && (
                <button
                  onClick={resetSession}
                  className="w-16 h-16 sm:w-20 sm:h-20 rounded-full bg-white/80 backdrop-blur shadow-lg flex items-center justify-center hover:bg-white transition-colors"
                  style={{ border: '3px solid rgba(239, 68, 68, 0.5)' }}
                  title="Réinitialiser"
                  data-testid="reduce-freq-reset-btn"
                >
                  <RotateCcw className="w-6 h-6 sm:w-7 sm:h-7 text-red-500" />
                </button>
              )}
            </div>

            {!isActive && (
              <div className="w-full max-w-md bg-white/90 backdrop-blur rounded-xl px-4 py-3 shadow-lg border border-slate-300 text-center">
                <p className="text-xs sm:text-sm text-slate-700 leading-relaxed">
                  Commencez avec une durée courte (2s) et augmentez progressivement
                  pour ralentir votre fréquence respiratoire.
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Instructions drawer (desktop only) */}
        <ExerciseInstructionsDrawer testIdPrefix="reduce-freq">
          <div className="bg-white rounded-xl px-4 py-3 shadow border border-slate-200">
            <h3 className="font-semibold text-slate-900 mb-2 text-sm">Conseil</h3>
            <p className="text-xs text-slate-700 leading-relaxed">
              Commencez avec une durée courte (2s) et augmentez progressivement
              pour ralentir votre fréquence respiratoire.
            </p>
          </div>

          <div className="bg-white rounded-xl shadow border border-slate-200 overflow-hidden">
            <div className="px-4 py-3 border-b border-slate-200 bg-gradient-to-r from-cyan-50 to-emerald-50">
              <h3 className="font-semibold text-slate-900 text-sm">Pourquoi cet exercice ?</h3>
              <p className="text-[11px] text-slate-500 mt-0.5">Respiration réduite — désensibilisation au CO₂</p>
            </div>
            <div className="px-4 py-3 space-y-3">
              <section>
                <h4 className="text-[11px] uppercase tracking-wider font-bold text-cyan-700 mb-1.5">Compétences travaillées</h4>
                <ul className="text-xs text-slate-700 space-y-1.5 list-disc pl-4">
                  <li><strong>Gestion du débit inspiratoire et expiratoire</strong> — apprendre à ralentir la vitesse à laquelle l'air entre et sort du nez.</li>
                  <li><strong>Prise de conscience de l'hyperventilation</strong> — reconnaître les moments où vous respirez trop fort.</li>
                  <li><strong>Automatisme de respiration douce</strong> — créer progressivement le réflexe corporel de respirer avec la plus grande douceur.</li>
                  <li><strong>Contrôle du volume respiratoire</strong> — limiter les volumes d'air à chaque respiration.</li>
                </ul>
              </section>
              <section>
                <h4 className="text-[11px] uppercase tracking-wider font-bold text-emerald-700 mb-1.5">Objectifs visés</h4>
                <ul className="text-xs text-slate-700 space-y-1 list-disc pl-4">
                  <li>Limiter la dépense énergétique de la respiration</li>
                  <li>Ralentir le débit d'air, souffler moins fort</li>
                  <li>Réduire les volumes d'air inspirés</li>
                  <li>Éviter l'hyperventilation chronique</li>
                  <li>Retrouver un CO₂ physiologique dans le sang</li>
                </ul>
              </section>
              <section>
                <h4 className="text-[11px] uppercase tracking-wider font-bold text-rose-700 mb-1.5">Éviter l'hyperventilation</h4>
                <p className="text-[11px] text-slate-600 italic mb-1.5">Prendre de grands volumes d'air = évacuer trop de CO₂, avec les conséquences suivantes :</p>
                <ul className="text-xs text-slate-700 space-y-1.5 list-disc pl-4">
                  <li><strong>Désoxygénation du corps</strong> — l'O₂ se lie à l'hémoglobine mais ne se libère plus dans les tissus.</li>
                  <li><strong>Resserrement des artères</strong> — diminution du flux sanguin vers les organes, muscles, cellules et cerveau.</li>
                  <li><strong>Resserrement des bronches</strong> — difficulté croissante à respirer et à expirer, sensation constante de manque d'air.</li>
                  <li><strong>Diminution de l'évacuation d'acide lactique</strong> à l'effort → fatigue musculaire rapide, crampes, douleurs, perte d'efficacité.</li>
                  <li><strong>Activation du système nerveux de stress</strong> — digestion et sommeil perturbés, pensées accélérées, anxiété.</li>
                  <li>Et bien d'autres effets liés à la désoxygénation…</li>
                </ul>
              </section>
              <section>
                <h4 className="text-[11px] uppercase tracking-wider font-bold text-amber-700 mb-1.5">Effets bénéfiques sur le corps</h4>
                <ul className="text-xs text-slate-700 space-y-1.5 list-disc pl-4">
                  <li><strong>Meilleure oxygénation</strong> des organes, muscles et cerveau (effet Bohr).</li>
                  <li><strong>Ouverture des bronches et des artères</strong> grâce au maintien du CO₂.</li>
                  <li><strong>Économie d'énergie</strong> — moins d'air brassé = moins d'effort respiratoire.</li>
                  <li><strong>Apaisement nerveux</strong> — bascule vers le parasympathique.</li>
                  <li><strong>Meilleure endurance et récupération</strong> — moins de crampes, moins d'essoufflement.</li>
                </ul>
              </section>
            </div>
          </div>
        </ExerciseInstructionsDrawer>
      </div>
      <AirHungerRatingModal
        isOpen={showAirHungerModal}
        onClose={handleAirHungerClose}
        sessionId={lastSessionId}
        onApplyRecommendation={applyAirHungerRecommendation}
      />
    </div>
  );
};

export default ReduceFrequencyModal;
